import BookshelfIcon from "@assets/compass.svg";
import { Authenticator, Label, useAuthenticator } from "@aws-amplify/ui-react";
import type { AccountInfo } from "@azure/msal-browser";
import * as msal from "@azure/msal-browser";
import { css } from "@emotion/react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { LoginOptions } from "../../../../App";
import { AuthenticatorContainer } from "./authenticatorContainer";
import { AuthenticatorLoading } from "./authenticatorLoading";
import { useAuthenticatorSettings } from "./useAuthenticatorSettings";
import {
  authenticateWithMicrosoftToken,
  msalConfig,
  msalSignOut,
  validateEmail,
} from "./utils";

import { PrimaryButton } from "../../../bits/buttons";
import { Input } from "../../../bits/inputs";
import { LoadingSpinner } from "../../../bits/loading";
import { useConfig } from "../../../hooks/useConfig";
import AuthenticatorBackground from "./AuthenticatorBackground";
import { AuthenticatorFooter } from "./AuthenticatorFooter";

export const CustomAuthenticator = ({
  resetAmplify,
  amplifyKey,
  children,
  loginOptions,
  setLoginOptions,
  setAuthError,
}: {
  resetAmplify: () => void;
  amplifyKey: number;
  children: React.ReactNode;
  loginOptions?: LoginOptions;
  setLoginOptions: React.Dispatch<
    React.SetStateAction<LoginOptions | undefined>
  >;
  setAuthError: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { user, authStatus, isPending } = useAuthenticator();
  const config = useConfig();
  const [emailInput, setEmailInput] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);

  const msalInstancePromise = useMemo(async () => {
    if (!config?.msalClientId) {
      return null;
    }
    const msalInstance = new msal.PublicClientApplication(
      msalConfig(config?.msalClientId ?? ""),
    );
    await msalInstance.initialize();
    return msalInstance;
    // reinitialize msal instance when user changes to ensure we have the latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setLoading(true);
    } else if (user && loading) {
      setLoading(false);
    }
  }, [user, loading]);

  // handle msal, resolve redirect, trigger login flow if there is an account in msal cache
  // also handle logging out from msal if user logs in with cognito
  useEffect(() => {
    if (!user && !isPending && authStatus === "unauthenticated") {
      (async () => {
        const msalInstance = await msalInstancePromise;
        if (!msalInstance) {
          return;
        }
        await msalInstance.handleRedirectPromise();

        const myAccounts: AccountInfo[] = msalInstance.getAllAccounts();

        if (myAccounts.length === 0) {
          msalInstance.clearCache();
          return;
        }

        if (myAccounts.length > 1) {
          msalSignOut(config?.msalClientId ?? "");
          msalInstance.clearCache();
          return;
        }

        // if there are accounts, log out to reset state. This is needed to handle the case where user logs in with msal, then logs out from cognito,
        // then tries to log in again with msal. Without this, msal will think user is still logged in and won't trigger the login flow, which causes issues with cognito state.
        if (myAccounts.length === 1) {
          setLoading(true);
          try {
            await authenticateWithMicrosoftToken(myAccounts[0]);
          } catch (error) {
            if (
              error instanceof Error &&
              error.message === "User does not exist."
            ) {
              setAuthError(
                `No account found for this Microsoft email - ${myAccounts[0].idTokenClaims?.email}`,
              );
            } else {
              console.error(
                "Failed to authenticate with Microsoft token",
                error,
              );
              setAuthError(
                "Failed to authenticate with Microsoft. Please try again.",
              );
            }
            msalSignOut(config?.msalClientId ?? "");
            msalInstance.clearCache();
          }
          resetAmplify();
          setLoading(false);
          return;
        }
      })();
    }
    if (user && !isPending && authStatus !== "configuring") {
      msalSignOut(config?.msalClientId ?? "");
    }
  }, [
    msalInstancePromise,
    user,
    isPending,
    authStatus,
    resetAmplify,
    config?.msalClientId,
    setAuthError,
  ]);

  // to reset amplify state when user signs out
  useEffect(() => {
    if (user && !isPending && !loggedIn) {
      setLoggedIn(true);
      setAuthError("");
    } else if (authStatus === "unauthenticated" && !isPending && loggedIn) {
      setLoggedIn(false);
      resetAmplify();
    }
  }, [user, authStatus, isPending, loggedIn, resetAmplify, setAuthError]);

  // reset email input and validation message when user logs in
  useEffect(() => {
    if (user) {
      setEmailInput("");
      setEmail("");
      setValidationMessage("");
    }
  }, [user]);

  // fetch login options when email is set
  useEffect(() => {
    setLoginOptions(undefined);
    (async () => {
      if (email) {
        try {
          const loginOptions = await (
            await fetch(
              `${import.meta.env.VITE_API_URL ?? window.location.origin}/getLoginOptions?email=${encodeURIComponent(email)}`,
            )
          ).json();

          setLoginOptions(loginOptions);
        } catch (error) {
          setAuthError("Failed to fetch login options. Please try again.");
          throw error;
        }
      }
    })();
  }, [email, setAuthError, setLoginOptions]);

  const authenticatorSettings = useAuthenticatorSettings({
    email,
    loginOptions,
    setLoginOptions,
    msalInstancePromise,
    setEmail,
    setEmailInput,
    resetAmplify,
  });

  const onSubmitEmail = useCallback(() => {
    if (!validateEmail(emailInput)) {
      setEmail("");
      setValidationMessage("Please enter a valid email address");
      return;
    }
    setEmail(emailInput);
    setValidationMessage("");
  }, [emailInput]);

  if (loading || authStatus === "configuring") {
    return (
      <LoadingSpinner
        css={css`
          width: 100%;
        `}
      />
    );
  }

  // email input
  if (!email && !user) {
    return (
      <AuthenticatorBackground>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            css={css`
              box-shadow: none;
              background-color: transparent;
              width: 212px;
              margin-bottom: 32px;
              padding-bottom: 32px;
              padding-left: 32px;
              @media (max-width: 1000px) {
                padding: 32px 32px 0px 32px;
              }
            `}
            src={BookshelfIcon}
            alt="Compass"
            title="Home"
          />
          <AuthenticatorContainer>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
                width: 100%;
              `}
            >
              <h1
                css={({ colours }) => css`
                  color: ${colours.primary.mid.dark20};
                `}
              >
                Log in to your account
              </h1>
              <Label
                htmlFor="email-input"
                css={css`
                  padding-bottom: 30px;
                `}
              >
                Enter your email address to continue
              </Label>
              <Input
                label="Email address"
                id="email-input"
                type="email"
                transparentBackground
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmailInput(e.currentTarget.value)
                }
                value={emailInput}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    onSubmitEmail();
                  }
                }}
              />
              {validationMessage && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.875rem",
                    alignSelf: "flex-start",
                  }}
                >
                  {validationMessage}
                </div>
              )}
              <PrimaryButton
                css={css`
                  height: 40px;
                `}
                onClick={() => {
                  onSubmitEmail();
                }}
              >
                Continue
                <ArrowForwardIcon />
              </PrimaryButton>
            </div>
          </AuthenticatorContainer>
          <AuthenticatorFooter />
        </div>
      </AuthenticatorBackground>
    );
  }

  if ((!loginOptions && !user) || isPending) {
    return (
      <AuthenticatorBackground>
        <AuthenticatorLoading />
      </AuthenticatorBackground>
    );
  }

  return (
    <AuthenticatorBackground user={user}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: user ? "100%" : undefined,
        }}
      >
        {!user && (
          <img
            css={css`
              box-shadow: none;
              background-color: transparent;
              width: 212px;
              margin-bottom: 32px;
              padding-bottom: 32px;
              padding-left: 32px;
              @media (max-width: 1000px) {
                padding: 32px 32px 0px 32px;
              }
            `}
            src={BookshelfIcon}
            alt="Compass"
            title="Home"
          />
        )}
        <Authenticator
          key={amplifyKey}
          css={css`
            fieldset.amplify-flex {
              ${loginOptions?.password ? "" : "display: none;"}
            }

            [data-amplify-form] {
              ${loginOptions?.password ? "" : "padding: 0 32px 32px 32px"}
            }

            .amplify-button--primary {
              ${loginOptions?.password ? "" : "display: none;"}
            }

            [readonly] {
              background-color: rgba(120, 120, 120, 0.05);
              color: rgba(120, 120, 120, 1);
            }

            [data-amplify-authenticator] [data-amplify-router] {
              background-color: var(
                --amplify-components-authenticator-router-background-color
              );
              box-shadow: var(
                --amplify-components-authenticator-router-box-shadow
              );
              border-color: var(
                --amplify-components-authenticator-router-border-color
              );
              border-width: var(
                --amplify-components-authenticator-router-border-width
              );
              border-style: var(
                --amplify-components-authenticator-router-border-style
              );
            }
          `}
          {...authenticatorSettings}
        >
          {children}
        </Authenticator>
        {!user && <AuthenticatorFooter />}
      </div>
    </AuthenticatorBackground>
  );
};
